import ViewerBanner from "@/components/ViewerBanner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Component, useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { classNames } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import WorkspaceChangedBanner from "@/components/WorkspaceChangedBanner";
import { onReread } from "@/lib/reread";
import { useTheme } from "@/context/ThemeContext";
import { useMyFollowUps, useInvalidateMyFollowUps } from "@/hooks/useMyFollowUps";
import GlobalSearch from "@/components/GlobalSearch";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { focusIfShown, focusTemporarily } from "@/lib/focus";
import {
  LayoutDashboard, Zap, Users, Bookmark,
  Sun, Moon, LogOut, Search, Menu, Sparkles, Plug, Wand2, UsersRound, Gavel, Settings,
} from "lucide-react";

const NAV_GROUPS = [
  {
    name: "WORKFLOW",
    items: [
      { name: "Home", to: "/", paths: ["/", "/analytics"], exact: true, icon: LayoutDashboard, testid: "nav-link-home" },
      { name: "Creators", to: "/creators", paths: ["/creators", "/watchlists", "/compare"], icon: Users, testid: "nav-link-creators" },
      { name: "Signals", to: "/outliers", paths: ["/outliers", "/trends"], icon: Zap, testid: "nav-link-signals" },
      { name: "Studio", to: "/hook-lab", paths: ["/hook-lab", "/patterns"], icon: Wand2, testid: "nav-link-studio" },
      { name: "Saved", to: "/saved", paths: ["/saved"], icon: Bookmark, testid: "nav-link-saved" },
      { name: "Decisions", to: "/decisions", paths: ["/decisions"], icon: Gavel, testid: "nav-link-decisions" },
    ],
  },
  {
    name: "WORKSPACE",
    items: [
      { name: "Team", to: "/team", paths: ["/team"], icon: UsersRound, testid: "nav-link-team" },
      { name: "Integrations", to: "/sources", paths: ["/sources"], icon: Plug, testid: "nav-link-integrations" },
    ],
  },
];

// Tailwind `md`: from this width the drawer and its menu button are hidden and the desktop sidebar is shown.
const DESKTOP_QUERY = "(min-width: 768px)";
const isDesktop = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(DESKTOP_QUERY).matches;

function isNavigationActive(item, pathname) {
  return item.paths.some((path) => pathname === path || (!item.exact && pathname.startsWith(`${path}/`)));
}

// The inherited switcher has its own read of /workspaces. A malformed result
// there must not remove the page's independent read/error state or navigation.
class WorkspaceReadBoundary extends Component {
  state = { unavailable: false };
  static getDerivedStateFromError() { return { unavailable: true }; }
  render() {
    return this.state.unavailable ? <p role="status" className="px-4 pt-3 text-xs text-muted-foreground">Sélecteur d’espace indisponible. Consultez <Link to="/team" onClick={this.props.onNavigate} className="text-primary underline">Team</Link> pour gérer vos espaces.</p> : this.props.children;
  }
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { data: fuData } = useMyFollowUps();
  const myFollowUps = { open: fuData?.open || 0, overdue: fuData?.overdue || 0 };
  const invalidateFollowUps = useInvalidateMyFollowUps();
  const firstRoute = useRef(true);
  const nav = useNavigate();
  const location = useLocation();
  const menuButton = useRef(null);
  const drawer = useRef(null);
  const main = useRef(null);
  const searchButton = useRef(null);

  useEffect(() => {
    if (firstRoute.current) firstRoute.current = false; else invalidateFollowUps();
    if (!user?.workspace_id) { setUnread(0); return; }
    api.get("/saved/unread").then(({ data }) => setUnread(data.unread_videos || 0)).catch(() => {});
  }, [user?.workspace_id, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // D10: read again when this page's workspace became current again (no navigation needed).
  useEffect(() => onReread(() => {
    if (user?.workspace_id) api.get("/saved/unread").then(({ data }) => setUnread(data.unread_videos || 0)).catch(() => {});
  }), [user?.workspace_id]);

  // Any navigation closes the drawer (links, workspace "Manage teams…", search results, history).
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  // The drawer only exists below `md`: widening the window closes it, which releases the focus trap,
  // the scroll lock and the hidden background.
  useEffect(() => {
    if (!mobileOpen || typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const closeOnDesktop = () => { if (query.matches) setMobileOpen(false); };
    closeOnDesktop();
    if (query.addEventListener) query.addEventListener("change", closeOnDesktop); else query.addListener(closeOnDesktop);
    return () => { if (query.removeEventListener) query.removeEventListener("change", closeOnDesktop); else query.removeListener(closeOnDesktop); };
  }, [mobileOpen]);

  // Start on the current destination (or the first one) rather than on the close button.
  const focusCurrentDestination = (event) => {
    const panel = drawer.current;
    const target = panel?.querySelector('nav a[aria-current="page"]') || panel?.querySelector("nav a");
    if (!target) return;
    event.preventDefault();
    target.focus();
  };
  // Back to the menu button while it is shown; from desktop width it is hidden, so go to the page content.
  // If another dialog (the global search) holds focus when the drawer goes away under it, leave focus
  // there: that dialog restores it when it closes.
  const restoreFocus = (event) => {
    event.preventDefault();
    if (document.activeElement?.closest('[role="dialog"], [role="alertdialog"]')) return;
    if (isDesktop() || !focusIfShown(menuButton.current)) focusTemporarily(main.current);
  };
  // Destination when the element focused before the search is gone or hidden: the header search button,
  // shown at every width, else the page content. Never the menu button, hidden from desktop width.
  const restoreAfterSearch = () => {
    if (!focusIfShown(searchButton.current)) focusTemporarily(main.current);
  };

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const sidebar = (variant) => (
    <aside
      className="flex h-full w-64 flex-col overflow-x-hidden border-r border-border bg-[hsl(var(--sidebar))] shrink-0"
      data-testid="sidebar"
    >
      <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </div>
        <div>
          <div className="font-display text-base font-extrabold leading-tight">OutlierPulse</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Viral intel</div>
        </div>
      </div>
      <WorkspaceReadBoundary key={`${variant}-${user?.workspace_id || "personal"}`} onNavigate={() => setMobileOpen(false)}><WorkspaceSwitcher variant={variant} /></WorkspaceReadBoundary>
      <nav className="flex-1 space-y-5 px-3 py-4 overflow-y-auto" aria-label="Main navigation">
        {NAV_GROUPS.map((group) => (
          <div key={group.name} className="space-y-1">
            <div role="presentation" className="px-3 pb-1 text-[10px] font-semibold tracking-widest text-muted-foreground">
              {group.name}
            </div>
            {group.items.map((item) => {
              const active = isNavigationActive(item, location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={item.testid}
                  aria-current={active ? "page" : undefined}
                  className={
                    `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                      active
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.name}
                  {item.to === "/saved" && unread > 0 && (
                    <span className="ml-auto flex items-center gap-1" title={`${unread} saved video${unread === 1 ? "" : "s"} with unread team notes`} data-testid="nav-saved-unread-dot">
                      <span className="h-2 w-2 rounded-full bg-[hsl(var(--rose))]" aria-hidden="true" />
                      <span className="sr-only">{`, ${unread} saved video${unread === 1 ? "" : "s"} with unread team notes`}</span>
                    </span>
                  )}
                  {item.to === "/decisions" && myFollowUps.open > 0 && (
                    <span
                      className={classNames("ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold font-mono leading-none", myFollowUps.overdue > 0 ? "bg-[hsl(var(--rose))] text-white" : "bg-[hsl(var(--amber))]/15 text-[hsl(var(--amber))]")}
                      title={`${myFollowUps.open} open follow-up${myFollowUps.open === 1 ? "" : "s"} assigned to you${myFollowUps.overdue ? ` · ${myFollowUps.overdue} overdue` : ""}`}
                      data-testid="nav-decisions-follow-up-count"
                    >
                      <span aria-hidden="true">{myFollowUps.open}</span>
                      <span className="sr-only">{`, ${myFollowUps.open} open follow-up${myFollowUps.open === 1 ? "" : "s"} assigned to you${myFollowUps.overdue ? `, ${myFollowUps.overdue} overdue` : ""}`}</span>
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-border px-3 py-3">
        <div className="flex items-center gap-3 rounded-md px-3 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold text-xs">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          <Button
            data-testid="settings-link-button"
            variant="ghost"
            className="h-8 gap-2 px-2 text-xs"
            onClick={() => { setMobileOpen(false); nav("/settings"); }}
            aria-label="Account & security settings"
          >
            <Settings className="h-4 w-4" aria-hidden="true" />
            Settings
          </Button>
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="h-8 gap-2 px-2 text-xs"
            onClick={doLogout}
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Log out
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
    <div className="flex h-screen bg-background text-foreground">
      {/* desktop sidebar */}
      <div className="hidden md:flex">{sidebar("desktop")}</div>

      {/* mobile drawer: modal dialog (focus kept inside, background hidden, Escape / close button / backdrop close it) */}
      <SheetContent
        ref={drawer}
        side="left"
        closeLabel="Close navigation"
        aria-describedby={undefined}
        onOpenAutoFocus={focusCurrentDestination}
        onCloseAutoFocus={restoreFocus}
        className="w-64 max-w-[85vw] gap-0 border-r-0 bg-transparent p-0 sm:max-w-none md:hidden"
        overlayClassName="bg-black/50 md:hidden"
        data-testid="mobile-nav-drawer"
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        {sidebar("mobile")}
      </SheetContent>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-background/80 backdrop-blur-md px-4 md:px-6 h-14">
          <SheetTrigger asChild>
            <Button
              ref={menuButton}
              variant="ghost"
              size="icon"
              className="md:hidden"
              data-testid="mobile-menu-toggle"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
          </SheetTrigger>

          <button
            ref={searchButton}
            onClick={() => setSearchOpen(true)}
            className="flex-1 min-w-0 max-w-md flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition-colors"
            data-testid="global-search-trigger"
          >
            <Search className="h-4 w-4 shrink-0" />
            {/* T1: one line whatever the font metrics; the full text stays in the accessible name. */}
            <span className="min-w-0 truncate">Search creators, videos, hooks…</span>
            <kbd data-testid="search-shortcut-label" className="ml-auto hidden sm:inline-block font-mono text-[10px] bg-background border border-border rounded px-1.5 py-0.5">{typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? "⌘K" : "Ctrl+K"}</kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle-button"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>

        <main ref={main} className="flex-1 overflow-y-auto focus:outline-none">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6 md:py-8">
            <WorkspaceChangedBanner />
            <ViewerBanner user={user} />
            {children}
          </div>
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} restoreFocusFallback={restoreAfterSearch} />
    </div>
    </Sheet>
  );
}
